import React from "react";

function DocClinicInfo({
  clinic = {},
  weeklySchedules = [],
  isLoading,
  isSuccess,
}) {
  const formatTime = (timeString) => {
    if (!timeString || timeString.toLowerCase() === "closed") return "Closed";

    const parts = timeString.split(":");
    const hh = parseInt(parts[0], 10) || 0;
    const mm = parseInt(parts[1], 10) || 0;

    const period = hh >= 12 ? "PM" : "AM";
    const hour12 = hh % 12 === 0 ? 12 : hh % 12;

    return `${hour12}:${String(mm).padStart(2, "0")} ${period}`;
  };

  if (isLoading) {
    return (
      <div
        style={{
          background: "#ffffff",
          borderRadius: 12,
          padding: 32,
          textAlign: "center",
        }}
      >
        <div
          className="spinner-border"
          role="status"
          style={{ width: 28, height: 28, borderWidth: 2.5, color: "#6366f1" }}
        />
        <div style={{ marginTop: 10, color: "#9ca3af", fontSize: 13 }}>
          Loading...
        </div>
      </div>
    );
  }

  if (isSuccess && !clinic?.clinic_Hospital_Name) {
    return (
      <div
        style={{
          background: "#ffffff",
          borderRadius: 12,
          padding: 40,
          textAlign: "center",
          color: "#9ca3af",
          fontSize: 13,
        }}
      >
        No clinic data available
      </div>
    );
  }

  return (
    <div
      style={{
        background: "#ffffff",
        borderRadius: 12,
        padding: "24px 20px",
      }}
    >
      {/* Clinic Header */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 20 }}>
        {/* Logo */}
        {clinic.logoUpload ? (
          <img
            src={clinic.logoUpload}
            alt="Clinic"
            style={{
              width: 56,
              height: 56,
              borderRadius: 8,
              objectFit: "cover",
              flexShrink: 0,
            }}
          />
        ) : (
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 8,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              fontSize: 22,
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {clinic.clinic_Hospital_Name?.[0] || "C"}
          </div>
        )}

        {/* Clinic Info */}
        <div style={{ flex: 1 }}>
          <h3
            style={{
              fontSize: 16,
              fontWeight: 600,
              color: "#1f2937",
              margin: 0,
              marginBottom: 4,
            }}
          >
            {clinic.clinic_Hospital_Name || "Unnamed Clinic"}
          </h3>
          <p
            style={{
              fontSize: 13,
              color: "#6b7280",
              margin: 0,
              lineHeight: 1.5,
            }}
          >
            {[clinic.clinicAddress, clinic.city, clinic.state, clinic.country]
              .filter(Boolean)
              .join(", ") || "N/A"}
          </p>
        </div>
      </div>

      {/* Weekly Schedule - Simple List */}
      <div>
        {weeklySchedules?.length > 0 ? (
          [...weeklySchedules]
            .sort((a, b) => a.weekdayOrder - b.weekdayOrder)
            .map((slot, idx) => {
              const isClosed =
                slot.startTime === "Closed" || slot.endTime === "Closed";
              const hasSecondSlot =
                slot.startTime1 &&
                slot.endTime1 &&
                slot.startTime1 !== "Closed" &&
                slot.endTime1 !== "Closed";

              return (
                <div
                  key={slot.weekday}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "12px 0",
                    borderBottom:
                      idx === weeklySchedules.length - 1
                        ? "none"
                        : "1px solid #f3f4f6",
                  }}
                >
                  {/* Day */}
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: "#4b5563",
                    }}
                  >
                    {slot.weekday}
                  </div>

                  {/* Time Slots */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      gap: 3,
                    }}
                  >
                    {isClosed ? (
                      <div
                        style={{
                          fontSize: 13,
                          color: "#9ca3af",
                          fontWeight: 500,
                        }}
                      >
                        Closed
                      </div>
                    ) : (
                      <>
                        {/* First Slot */}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            fontSize: 13,
                            color: "#1f2937",
                            fontWeight: 500,
                          }}
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            style={{ color: "#e8bbae", flexShrink: 0 }}
                          >
                            <circle cx="12" cy="12" r="10" />
                            <polyline points="12 6 12 12 16 14" />
                          </svg>
                          {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                        </div>

                        {/* Second Slot */}
                        {hasSecondSlot && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              fontSize: 13,
                              color: "#1f2937",
                              fontWeight: 500,
                            }}
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              style={{ color: "#e8bbae", flexShrink: 0 }}
                            >
                              <circle cx="12" cy="12" r="10" />
                              <polyline points="12 6 12 12 16 14" />
                            </svg>
                            {formatTime(slot.startTime1)} -{" "}
                            {formatTime(slot.endTime1)}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })
        ) : (
          <div
            style={{
              textAlign: "center",
              padding: 20,
              color: "#9ca3af",
              fontSize: 13,
            }}
          >
            No schedule available
          </div>
        )}
      </div>
    </div>
  );
}

export default DocClinicInfo;
